import React, { useState, useEffect, useContext } from 'react'
import styled from 'styled-components'
import { string, array, number, func, object, bool } from 'prop-types'
import { rgba } from 'polished'

import { pricify } from 'utils/pricify'
import { CARTRIDGES } from 'utils/constants'
import { trackEvent } from 'utils/tracking'
import { availableForSale } from 'utils/products'

import CheckoutContext from 'contexts/Checkout'
import NativeAuthContext from 'components/NativeAuth'
import { media } from 'styles/media'
import { white, red, black, grey, greyLight, greyDark, borderColor } from 'styles/colors'
import { boxShadow, cover, animationTime, animationCurve, borderRadius } from 'styles/global'
import { H4, fontWeights, fontSizes } from 'styles/typography'
import * as spacing from 'styles/spacing'

import Portal from 'components/Portal'
import QuantityPicker from 'components/QuantityPicker'
import Button from 'components/button/Button'
import Icon from 'components/Icon'
import Toggle from 'components/Toggle'
import useDigitalData from 'hooks/useDigitalData'

import CloseSmall from 'icons/close-small.svg'

const Container = styled.div`
  background-color: ${rgba(black, 0.75)};
  z-index: 900;
  display: flex;
  align-items: flex-end;
  ${cover('fixed')};

  ${media.small`
    align-items: center;
    justify-content: center;
  `};
`

const Overlay = styled.div`
  ${cover('absolute')};
`

const Inner = styled.div`
  position: relative;
  width: 100%;
  background-color: ${white};
  z-index: 10;

  ${media.small`
    width: 390px;
    ${boxShadow};
  `};
`

const Close = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  padding: ${spacing.medium};
  display: flex;
  color: ${greyLight};
  transition: color ${animationTime} ${animationCurve};

  &:hover {
    color: ${grey};
  }
`

const Header = styled.header`
  padding-top: ${spacing.small};
  padding-right: ${spacing.medium};
  margin-bottom: ${spacing.large};

  ${media.medium`
    margin-bottom: ${spacing.xLarge};
  `};
`

const Main = styled.main`
  padding: ${spacing.xLarge};

  ${media.medium`
    padding: ${spacing.xLarge} ${spacing.xxLarge};
  `};
`

const RowHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing.medium};
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  & + & {
    margin-top: ${spacing.medium};
  }
`

const Strength = styled.span`
  font-weight: ${fontWeights.bold};
  color: ${greyDark};
`

const Footer = styled.footer`
  display: flex;
  align-items: center;
  padding: ${spacing.medium} ${spacing.large};
  border-top: 1px solid ${borderColor};
`

const Label = styled.span`
  display: block;
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.tiny};
`

const StyledButton = styled(Button)`
  min-width: 160px;
`

const Price = styled.span`
  display: block;
  font-weight: ${fontWeights.bold};
  margin-top: -${spacing.tiny};
  color: ${greyDark};
`

const PriceWrap = styled.div`
  display: flex;
  flex-direction: column;
  text-align: right;
  margin-left: auto;
  margin-right: ${spacing.medium};
`

const Description = styled.p`
  font-size: ${fontSizes.small};
`

export const UnavailableTag = styled.span`
  display: block;
  font-size: ${fontSizes.tiny};
  color: ${red};
  font-weight: ${fontWeights.medium};
  margin-left: ${spacing.small};
  margin-right: auto;
  padding: 2px 5px;
  border-radius: ${borderRadius};
  background-color: ${rgba(red, 0.15)};
`

const Subscription = styled.div`
  margin-top: ${spacing.medium};
  display: flex;
`

const SubscriptionLabel = styled.span`
  display: block;
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.medium};
  color: ${black};
  margin-bottom: ${spacing.small};
`

const SubscriptionDisclaimer = styled.span`
  display: block;
  font-size: 11px;
`

const CartridgeModal = ({
  name,
  variants,
  close = () => {},
  onAdd,
  priceMultiplier = 1,
  product,
  currentCartridgeQuantity,
  deviceHandle,
  buttonLabel,
  showSubscription,
  multipackLimit,
  onToggleClick,
  toggleChecked,
  overrideNoQuantity
}) => {
  const { products } = useContext(CheckoutContext)
  const { province } = useContext(NativeAuthContext)
  const { digitalData } = useDigitalData()
  const [loading, setLoading] = useState(false)

  const fetchedProduct = products.find(p => product.id.includes(p.shopifyGid))

  useEffect(() => {
    if (products) {
      window.dataObject.time = Date()
      trackEvent({
        name: 'modalProdView',
        time: Date(),
        product: [product]
      })

      window.dataObject.viewProduct = [
        {
          availability: product.variants.every(
            v => v.availableForSale && fetchedProduct?.availableProvinces.includes(province)
          ),
          prodID: product.id,
          name: product.title,
          priceUnit: product.priceRange.maxVariantPrice.amount,
          priceTotal: product.priceRange.maxVariantPrice.amount
        }
      ]

      return () => {
        delete window.dataObject.viewProduct
      }
    }
  }, [product, products])

  useEffect(() => {
    try {
      digitalData.push({
        page: {
          virtualUrl: location.pathname + 'cartridge-modal',
          timestamp: new Date().getTime()
        }
      })
    } catch (e) {
      console.log(e)
    }

    return () => {
      digitalData.push({
        page: {
          virtualUrl: null
        }
      })
    }
  }, [])

  const cartridgesQuantity = currentCartridgeQuantity ? currentCartridgeQuantity() : null
  const [localVariants, setLocalVariants] = useState(variants)

  const packPrice = parseFloat(product.priceRange.maxVariantPrice.amount)
  const quantities = localVariants.map(variant => variant.quantity)
  const quantitiesSum = quantities.reduce((a, b) => {
    return a + b
  }, 0)

  const totalPrice = { amount: quantitiesSum * packPrice }

  const handleQuantityChange = (value, variantTitle) => {
    const upToDateLocalVariants = [...localVariants]
    const localVariantIndex = localVariants.findIndex(variant => variant.title === variantTitle)
    upToDateLocalVariants[localVariantIndex] = {
      ...upToDateLocalVariants[localVariantIndex],
      quantity: upToDateLocalVariants[localVariantIndex].quantity + value
    }
    setLocalVariants(upToDateLocalVariants)
  }

  if (localVariants) {
    return (
      <Portal selector="modals">
        <Container>
          <Overlay onClick={close} />
          <Inner>
            <Close onClick={close}>
              <Icon icon={<CloseSmall />} />
            </Close>
            <Main>
              <Header>
                {product.handle === 'garden-strawberry' && <Label>Pack (2 cartridges)</Label>}
                {product.handle === 'discovery-pack-epen-3-vpro-cartridges' && <Label>{`Pack (6 cartridges)`}</Label>}
                {product.handle === 'epod-multi-pack' && <Label>{`Pack (4 cartridges)`}</Label>}
                {product.handle !== 'garden-strawberry' &&
                  product.handle !== 'discovery-pack-epen-3-vpro-cartridges' &&
                  product.handle !== 'epod-multi-pack' &&
                  deviceHandle && <Label>{`Pack (${CARTRIDGES[deviceHandle].packName})`}</Label>}
                <H4>{name}</H4>
                {product.description && <Description>{product.description}</Description>}
              </Header>
              <RowHeader>
                <Label>Nicotine strength</Label>
                <Label
                  css={`
                    text-align: center;
                    width: 108px;
                  `}
                >
                  Pack quantity
                </Label>
              </RowHeader>
              {variants.map((variant, i) => {
                const fetchedVariant = fetchedProduct?.variants.find(v => variant.id.includes(v.shopifyGid))
                const isAvailableForSale = availableForSale({ variant: fetchedVariant, province })

                if (!fetchedVariant?.availableProvinces.includes(province)) return null

                return (
                  <Row key={variant.title}>
                    <Strength>
                      {product.handle === 'discovery-pack-epen-3-vpro-cartridges' ? '30mg/ml' : variant.title}
                    </Strength>
                    {!isAvailableForSale && <UnavailableTag>Unavailable</UnavailableTag>}
                    <QuantityPicker
                      onChange={handleQuantityChange}
                      value={localVariants[i].quantity}
                      title={variant.title}
                      product={product}
                      disableAdd={multipackLimit && quantitiesSum + cartridgesQuantity >= multipackLimit}
                      isAvailableForSale={isAvailableForSale}
                      shadow
                    />
                  </Row>
                )
              })}
              {showSubscription && (
                <Subscription>
                  <div
                    css={`
                      flex: 1;
                      padding-top: 6px;
                      padding-right: ${spacing.medium};
                    `}
                  >
                    <SubscriptionLabel>Make it a Subscription and save big?</SubscriptionLabel>
                    <SubscriptionDisclaimer>
                      Buy <strong>3-4 packs</strong> of any flavour & <strong>save 20%</strong> or{' '}
                      <strong>Buy 5 or above</strong> of any flavour & <strong>save 25%</strong>. Auto-shipped and
                      billed every 30 days. Free shipping on all subscription orders.
                    </SubscriptionDisclaimer>
                  </div>
                  <Toggle onClick={onToggleClick} checked={toggleChecked} />
                </Subscription>
              )}
            </Main>
            <Footer>
              <PriceWrap>
                <Label>Total</Label>
                <Price>{pricify({ amount: totalPrice.amount * priceMultiplier })}</Price>
              </PriceWrap>
              <StyledButton
                onClick={async () => {
                  setLoading(true)
                  await onAdd(product, localVariants, product.id)
                  setLoading(false)
                  close()
                }}
                theme="primary"
                disabled={(!overrideNoQuantity && quantitiesSum === 0) || loading}
              >
                {buttonLabel}
              </StyledButton>
            </Footer>
          </Inner>
        </Container>
      </Portal>
    )
  }

  return null
}

CartridgeModal.propTypes = {
  name: string.isRequired,
  handle: string,
  releaseDate: string,
  variants: array.isRequired,
  deviceHandle: string,
  close: func.isRequired,
  onAdd: func.isRequired,
  calculateTotalPrice: func,
  currentCartridgeQuantity: func,
  priceMultiplier: number,
  multipackLimit: number,
  product: object,
  flow: string,
  showSubscription: bool,
  buttonLabel: string,
  onToggleClick: func,
  toggleChecked: bool,
  overrideNoQuantity: bool
}

export default CartridgeModal
